"use client";

import { useState } from "react";
import { CONSENT_COOKIE_NAME, serializeConsentCookie } from "@/lib/analytics/consent";

interface Props {
  enabled: boolean;
  bannerText: string;
  acceptLabel: string;
  declineLabel: string;
  bannerColor?: string;
}

/**
 * Renders only when (props.enabled === true && no decision cookie present at
 * mount). The server-side AnalyticsInjector reads the same cookie and decides
 * whether to emit tracker snippets — they stay in sync because Accept reloads
 * the page.
 */
export function ConsentBanner(props: Props) {
  const [dismissed, setDismissed] = useState(() => {
    if (!props.enabled) {
      return true;
    }
    if (typeof document === "undefined") {
      return false;
    }
    return document.cookie
      .split(";")
      .map((c) => c.trim())
      .some(
        (c) =>
          c.startsWith(`${CONSENT_COOKIE_NAME}=granted`) ||
          c.startsWith(`${CONSENT_COOKIE_NAME}=declined`),
      );
  });

  if (!props.enabled || dismissed) {
    return null;
  }

  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";

  const accept = () => {
    document.cookie = serializeConsentCookie("granted", { secure: isSecure });
    window.location.reload();
  };

  const decline = () => {
    document.cookie = serializeConsentCookie("declined", { secure: isSecure });
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2147483640,
        padding: "16px 20px",
        backgroundColor: props.bannerColor ?? "#1a1a1a",
        color: "#fff",
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <p style={{ margin: 0, flex: "1 1 240px", fontSize: 14 }}>{props.bannerText}</p>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={decline}
          style={{
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.35)",
            padding: "8px 16px",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {props.declineLabel}
        </button>
        <button
          type="button"
          onClick={accept}
          style={{
            background: "#fff",
            color: "#1a1a1a",
            border: "none",
            padding: "8px 16px",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          {props.acceptLabel}
        </button>
      </div>
    </div>
  );
}
